import className from 'classnames/bind';
import { motion } from 'motion/react';
import { useState, useEffect } from 'react';

import styles from './SoundIcon.module.scss';
const cx = className.bind(styles);

// Color palette based on intensity
const GREY = '#505152';
const YELLOW = '#ffff4b';
const GREEN = '#9efd62';
const PINK = '#d63c97';

// Generate colors for a column based on intensity (0-1)
function getColorsForIntensity(intensity: number, usePink: boolean = false): string[] {
  const greenBar = GREEN; // Top bar always green

  if (intensity < 0.2) {
    // Very low - mostly grey
    return [GREY, GREY, GREY, GREY, greenBar];
  } else if (intensity < 0.4) {
    // Low - one yellow
    return [GREY, GREY, GREY, YELLOW, greenBar];
  } else if (intensity < 0.6) {
    // Medium - two yellow
    return [GREY, GREY, YELLOW, YELLOW, greenBar];
  } else if (intensity < 0.8) {
    // High - two yellow + maybe pink
    if (usePink && Math.random() > 0.5) {
      return [GREY, PINK, YELLOW, YELLOW, greenBar];
    }
    return [GREY, GREY, YELLOW, YELLOW, greenBar];
  } else {
    // Very high - pink moments
    if (usePink && Math.random() > 0.3) {
      return [PINK, PINK, YELLOW, YELLOW, greenBar];
    }
    return [GREY, PINK, YELLOW, YELLOW, greenBar];
  }
}

function AnimatedColumn({ columnIndex, animating }: { columnIndex: number, animating: boolean }) {
  const [colors, setColors] = useState<string[]>(() =>
    getColorsForIntensity(0.3, false)
  );

  useEffect(() => {
    // Each column has slightly different timing to simulate different frequency ranges
    const baseInterval = 120 + columnIndex * 30; // 120ms, 150ms, 180ms

    const updateColumn = () => {
      // Generate organic intensity with some randomness
      const baseIntensity = Math.random();

      // Add wave-like variation
      const time = Date.now() / 1000;
      const wave = Math.sin(time * (1 + columnIndex * 0.3)) * 0.3;
      const intensity = Math.max(0, Math.min(1, baseIntensity + wave));

      // Middle column more likely to show pink (simulate bass/impact)
      const usePink = columnIndex === 1;

      setColors(getColorsForIntensity(intensity, usePink));

      // Vary the next interval slightly for more organic feel
      const nextInterval = baseInterval + (Math.random() - 0.5) * 60;
      setTimeout(updateColumn, Math.max(80, nextInterval));
    };

    // Start with a staggered delay
    const initialDelay = columnIndex * 40;
    const timeout = setTimeout(updateColumn, initialDelay);

    return () => clearTimeout(timeout);
  }, [columnIndex]);

  return (
    <div className={cx("sound-icon-column")}>
      {colors.map((color, barIndex) => (
        <motion.div
          key={barIndex}
          className={cx("sound-icon-bar")}
          animate={animating ? { backgroundColor: color } : { backgroundColor: GREY }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            mass: 0.5
          }}
          data-name="Bar"
        />
      ))}
    </div>
  );
}

export default function SoundIcon({ animating, className = '' }: { animating: boolean, className?: string }) {
  return (
    <div className={cx("sound-icon", className)}>
      {[0, 1, 2].map((colIndex) => (
        <AnimatedColumn key={colIndex} columnIndex={colIndex} animating={animating} />
      ))}
    </div>
  );
}
